import React, { createContext, useContext, useState, useEffect } from 'react';
import { contentService, DEFAULT_BLOG_POSTS } from '../services/contentService';
import { ToastContainer, ToastMessage } from '../components/ui/Toast';
import { BlogPost, HeroContent, Page, MenuItem, BrandingConfig } from '../types';

interface ContentContextType {
    heroContent: Record<string, HeroContent>;
    getHeroContent: (pageId: string) => HeroContent;
    blogPosts: BlogPost[];
    pages: Page[];
    menuItems: MenuItem[];
    updateHeroContent: (pageId: string, content: HeroContent) => Promise<void>;
    addBlogPost: (post: BlogPost) => Promise<void>;
    updateBlogPost: (post: BlogPost) => Promise<void>;
    deleteBlogPost: (id: string) => Promise<void>;
    savePage: (page: Page) => Promise<void>;
    deletePage: (id: string) => Promise<void>;
    saveMenuItems: (items: MenuItem[]) => Promise<void>;
    branding: BrandingConfig;
    updateBranding: (config: BrandingConfig) => Promise<void>;
}

const defaultHeroContent: HeroContent = {
    type: 'static',
    title: 'Cyber awareness, run for you',
    subtitle:
        'Phishing simulations, training assignments, and compliance reporting in one multi-tenant platform — for SMBs, MSSPs, and the communities that serve them.',
    ctaText: 'Start free',
    overlayEnabled: true,
    overlayColor: '#000000',
    overlayOpacity: 0.5,
    items: [
        { id: '1', url: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80', alt: 'Cybersecurity Background' }
    ]
};

const ContentContext = createContext<ContentContextType | undefined>(undefined);

export const ContentProvider = ({ children }: { children: React.ReactNode }) => {
    const [heroContentMap, setHeroContentMap] = useState<Record<string, HeroContent>>({});
    const [blogPosts, setBlogPosts] = useState<BlogPost[]>(DEFAULT_BLOG_POSTS);
    const [pages, setPages] = useState<Page[]>([]);
    const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
    const [toasts, setToasts] = useState<ToastMessage[]>([]);

    const showToast = (type: 'success' | 'error' | 'info', message: string) => {
        const id = Date.now().toString();
        setToasts(prev => [...prev, { id, type, message }]);
    };

    const removeToast = (id: string) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    };

    useEffect(() => {
        const loadContent = async () => {
            try {
                // Load hero content for all known pages
                const pagesList = ['home', 'training', 'resources', 'threats'];
                const heroPromises = pagesList.map(page => contentService.getHeroContent(page));
                const [posts, fetchedPages, fetchedMenu, ...heroes] = await Promise.all([
                    contentService.getBlogPosts(),
                    contentService.getPages(),
                    contentService.getMenuItems(),
                    ...heroPromises
                ]);

                const newHeroMap: Record<string, HeroContent> = {};
                pagesList.forEach((page, index) => {
                    newHeroMap[page] = heroes[index];
                });

                setHeroContentMap(newHeroMap);
                setBlogPosts(posts);
                setPages(fetchedPages);
                setMenuItems(fetchedMenu);

                // Load branding
                const brandingConfig = await contentService.getBranding();
                setBranding(brandingConfig);
            } catch (error) {
                console.error('Failed to load content:', error);
                showToast('error', 'Failed to load content');
            }
        };
        loadContent();
    }, []);

    const getHeroContent = (pageId: string) => {
        return heroContentMap[pageId] || defaultHeroContent; // defaultHeroContent needs to be defined
    };

    const updateHeroContent = async (pageId: string, content: HeroContent) => {
        try {
            const updated = await contentService.updateHeroContent(pageId, content);
            setHeroContentMap(prev => ({ ...prev, [pageId]: updated }));
            showToast('success', 'Hero section updated successfully');
        } catch (error: any) {
            console.error('Failed to update hero section:', error);
            if (error.name === 'QuotaExceededError' || error.message?.includes('QuotaExceededError')) {
                showToast('error', 'Storage full. Please use smaller images or fewer slides.');
            } else {
                showToast('error', 'Failed to update hero section');
            }
        }
    };

    const addBlogPost = async (post: BlogPost) => {
        try {
            const saved = await contentService.addBlogPost(post);
            setBlogPosts(prev => [saved, ...prev]);
            showToast('success', 'Blog post created successfully');
        } catch (error: any) {
            showToast('error', error.message || 'Failed to create blog post');
            throw error;
        }
    };

    const updateBlogPost = async (post: BlogPost) => {
        try {
            const saved = await contentService.updateBlogPost(post);
            setBlogPosts(prev => prev.map(p => p.id === post.id ? saved : p));
            showToast('success', 'Blog post updated successfully');
        } catch (error: any) {
            showToast('error', error.message || 'Failed to update blog post');
            throw error;
        }
    };

    const deleteBlogPost = async (id: string) => {
        try {
            await contentService.deleteBlogPost(id);
            setBlogPosts(prev => prev.filter(p => p.id !== id));
            showToast('success', 'Blog post deleted successfully');
        } catch (error) {
            showToast('error', 'Failed to delete blog post');
        }
    };

    // --- Dynamic Pages Context ---
    const savePage = async (page: Page) => {
        try {
            const saved = await contentService.savePage(page);
            setPages(prev => {
                const index = prev.findIndex(p => p.id === page.id);
                if (index >= 0) {
                    return prev.map(p => p.id === page.id ? saved : p);
                }
                return [...prev, saved];
            });
            showToast('success', 'Page saved successfully');
        } catch (error: any) {
            showToast('error', error.message || 'Failed to save page');
        }
    };

    const deletePage = async (id: string) => {
        try {
            await contentService.deletePage(id);
            setPages(prev => prev.filter(p => p.id !== id));
            showToast('success', 'Page deleted successfully');
        } catch (error) {
            showToast('error', 'Failed to delete page');
        }
    };

    // --- Using Branding ---
    const [branding, setBranding] = useState<BrandingConfig>({});

    useEffect(() => {
        // Update favicon dynamically
        if (branding.faviconUrl) {
            const link: HTMLLinkElement = document.querySelector("link[rel*='icon']") || document.createElement('link');
            link.type = 'image/x-icon';
            link.rel = 'shortcut icon';
            link.href = branding.faviconUrl;
            document.getElementsByTagName('head')[0].appendChild(link);
        }

        // Apply Theme
        if (branding.theme === 'dark') {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
    }, [branding.faviconUrl, branding.theme]);

    // --- Menu Context ---
    const saveMenuItems = async (items: MenuItem[]) => {
        try {
            const saved = await contentService.saveMenuItems(items);
            setMenuItems(saved);
            showToast('success', 'Menu updated successfully');
        } catch (error: any) {
            showToast('error', error.message || 'Failed to update menu');
        }
    };

    const updateBranding = async (config: BrandingConfig) => {
        try {
            const saved = await contentService.saveBranding(config);
            setBranding(saved);
            showToast('success', 'Branding updated successfully');
        } catch (error: any) {
            showToast('error', error.message || 'Failed to update branding');
        }
    };

    return (
        <ContentContext.Provider value={{
            heroContent: heroContentMap,
            getHeroContent,
            blogPosts,
            pages,
            menuItems,
            updateHeroContent,
            addBlogPost,
            updateBlogPost,
            deleteBlogPost,
            savePage,
            deletePage,
            saveMenuItems,
            branding,
            updateBranding,
        }}>
            {children}
            <ToastContainer toasts={toasts} removeToast={removeToast} />
        </ContentContext.Provider>
    );
};

export const useContent = () => {
    const context = useContext(ContentContext);
    if (context === undefined) {
        throw new Error('useContent must be used within a ContentProvider');
    }
    return context;
};


